import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container, Row, Col, Card, Form, Button, Badge, Modal,
  Spinner, Alert, InputGroup, ListGroup, Table,
} from 'react-bootstrap';
import { useInterfaceStore } from '../../stores/useInterfaceStore';
import api from '../../services/api';
import { notify } from '../../stores/useNotificationStore';
import CloneQuestionnaireModal, { CloneOptions } from '../../components/CloneQuestionnaireModal';
import { FiSend, FiCheck, FiClock, FiXCircle } from '../../utils/icons';

// ── Types ──────────────────────────────────────────────────────────────────────

type TipoResposta = 'SIM_NAO' | 'PERCENTUAL' | 'TEXTO_LIVRE' | 'MULTIPLA_ESCOLHA';
type Nivel = 'principal' | 'complementar';
type Acao = 'CHECKLIST' | 'COMPLEMENTAR' | 'PROXIMA_PRINCIPAL' | 'FIM';
type NivelControle = 'MANDATORIO' | 'RECOMENDADO' | 'DESEJAVEL';

const NIVEL_CONTROLE_OPTIONS: { value: NivelControle; label: string }[] = [
  { value: 'MANDATORIO', label: 'Mandatório' },
  { value: 'RECOMENDADO', label: 'Recomendado' },
  { value: 'DESEJAVEL', label: 'Desejável' },
];

interface Condicao {
  resposta_valor: string;        // 'SIM' | 'NAO'
  acao: Acao;
  ordem: number;
  complementar_temp_id: string | null;
}

// Snapshot dos valores do Cadastro de Perguntas no momento em que a pergunta foi
// adicionada ao questionário — usado para exibir "Do cadastro" / "Alterar" e para
// restaurar o valor original.
interface CatalogoSnapshot {
  tipo_resposta: TipoResposta;
  peso: number;
  checklist_id: number | null;
  permite_na: boolean;
}

interface Overrides {
  tipo_resposta: boolean;
  peso: boolean;
  checklist_id: boolean;
  permite_na: boolean;
  // Não tem valor de "cadastro" real (a pergunta não tem esse atributo) — o padrão é
  // sempre false, então "overridden" aqui coincide com o próprio valor de ignorar_equivalencia.
  ignorar_equivalencia: boolean;
}

interface ChecklistItemPers {
  descricao: string;
  ativo: boolean;          // Nível de Maturidade: Sim = ativo / Não = inativo
  percentual_peso: number;
  nivel_controle?: NivelControle;
}

interface QItem {
  temp_id: string;
  pergunta_id: number;
  pergunta_text: string;
  tipo_resposta: TipoResposta;
  peso: number;
  checklist_id: number | null;
  permite_na: boolean;
  // Não propaga (envia nem recebe) resposta por equivalência para esta ocorrência.
  ignorar_equivalencia: boolean;
  nivel: Nivel;
  parent_temp_id: string | null;
  ordem: number;
  condicoes: Condicao[];
  catalogo: CatalogoSnapshot;
  overrides: Overrides;
  // Checklist personalizado só para esta pergunta no questionário.
  checklist_personalizado?: boolean;
  checklist_itens?: ChecklistItemPers[];
}

// Sentinel de checklist_id quando o checklist é personalizado e ainda não foi persistido
// (o backend cria o checklist real ao salvar). Serve apenas para indicar "ligado".
const CHECKLIST_PERSONALIZADO_ID = -1;

interface Pergunta {
  id: number;
  pergunta: string;
  tipo_resposta: TipoResposta;
  peso?: number;
  checklist_id?: number | null;
  permite_na?: boolean;
}

interface Framework { id: number; nome: string; descricao?: string; }

// ── Enviar Questionário aos Fornecedores ────────────────────────────────────

interface FornecedorEscopoRow {
  contrato_escopo_id: number;
  contrato_id: number;
  fornecedor_nome: string;
  representante_nome: string;
  representante_email: string;
}

type StatusEnvioItem = 'aguardando' | 'enviando' | 'enviado' | 'erro';

interface ProgressoEnvioItem {
  contrato_escopo_id: number;
  fornecedor_nome: string;
  status: StatusEnvioItem;
}

let _tempCounter = 0;
const newTempId = () => `t${++_tempCounter}`;

const noOverrides = (): Overrides => ({
  tipo_resposta: false, peso: false, checklist_id: false, permite_na: false, ignorar_equivalencia: false,
});

// ── Predictive search component ────────────────────────────────────────────────

interface PredictiveProps<T extends { id: number; nome?: string; pergunta?: string }> {
  items: T[];
  displayKey: keyof T;
  value: string;
  placeholder?: string;
  onSelect: (item: T) => void;
  onClear?: () => void;
  disabled?: boolean;
}

function PredictiveSearch<T extends { id: number; nome?: string; pergunta?: string }>({
  items, displayKey, value, placeholder, onSelect, onClear, disabled,
}: PredictiveProps<T>) {
  const [text, setText] = useState(value);
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => { setText(value); }, [value]);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const filtered = text.trim().length >= 1
    ? items.filter(i => String(i[displayKey] ?? '').toLowerCase().includes(text.toLowerCase()))
    : items.slice(0, 30);

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <InputGroup>
        <Form.Control
          value={text}
          onChange={e => { setText(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          placeholder={placeholder || 'Pesquisar...'}
          disabled={disabled}
        />
        {onClear && value && (
          <Button variant="outline-secondary" size="sm" onClick={() => { setText(''); onClear(); }}>×</Button>
        )}
      </InputGroup>
      {open && filtered.length > 0 && (
        <div style={{
          position: 'absolute', zIndex: 1050, width: '100%',
          background: '#fff', border: '1px solid #dee2e6',
          borderRadius: '0 0 4px 4px', maxHeight: 220, overflowY: 'auto', boxShadow: '0 4px 12px rgba(0,0,0,.15)',
        }}>
          {filtered.map(item => (
            <div
              key={item.id}
              style={{ padding: '8px 12px', cursor: 'pointer' }}
              onMouseDown={e => e.preventDefault()}
              onClick={() => { onSelect(item); setText(String(item[displayKey] ?? '')); setOpen(false); }}
              onMouseEnter={e => (e.currentTarget.style.background = '#f8f9fa')}
              onMouseLeave={e => (e.currentTarget.style.background = '')}
            >
              {String(item[displayKey] ?? '')}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Question picker modal ──────────────────────────────────────────────────────

interface PickerModalProps {
  show: boolean;
  onHide: () => void;
  perguntas: Pergunta[];
  onSelect: (p: Pergunta) => void;
  title?: string;
}

const QuestionPickerModal: React.FC<PickerModalProps> = ({ show, onHide, perguntas, onSelect, title }) => {
  const [search, setSearch] = useState('');
  const filtered = perguntas.filter(p => p.pergunta.toLowerCase().includes(search.toLowerCase()));

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>{title || 'Selecionar Pergunta'}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form.Control
          className="mb-3"
          placeholder="Filtrar perguntas..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          autoFocus
        />
        <ListGroup style={{ maxHeight: 400, overflowY: 'auto' }}>
          {filtered.length === 0 && <ListGroup.Item className="text-muted">Nenhuma pergunta encontrada</ListGroup.Item>}
          {filtered.map(p => (
            <ListGroup.Item
              key={p.id}
              action
              onClick={() => { onSelect(p); onHide(); }}
              className="d-flex justify-content-between align-items-center"
            >
              <span style={{ fontSize: '0.9rem' }}>{p.pergunta}</span>
              <Badge bg={tipoColor(p.tipo_resposta)} className="ms-2 text-nowrap">{tipoLabel(p.tipo_resposta)}</Badge>
            </ListGroup.Item>
          ))}
        </ListGroup>
      </Modal.Body>
    </Modal>
  );
};

// ── "Do cadastro" / "Alterar" toggle ────────────────────────────────────────────

interface CadastroToggleProps {
  overridden: boolean;
  disabled?: boolean;
  onToggle: (next: boolean) => void;
}

const CadastroToggle: React.FC<CadastroToggleProps> = ({ overridden, disabled, onToggle }) => (
  <div className="btn-group btn-group-sm ms-2" role="group">
    <Button
      size="sm"
      variant={!overridden ? 'secondary' : 'outline-secondary'}
      onClick={() => onToggle(false)}
      disabled={disabled}
      title="Usar o valor do Cadastro de Perguntas"
    >
      Do cadastro
    </Button>
    {/* Uma vez customizado, este botão vira um atalho pra restaurar (mesmo efeito de
        clicar em "Do cadastro") — deixa claro, sem precisar do texto ao lado, que o
        campo está sobrescrito e como voltar ao padrão. */}
    <Button
      size="sm"
      variant={overridden ? 'primary' : 'outline-primary'}
      onClick={() => onToggle(!overridden)}
      disabled={disabled}
      title={overridden ? 'Restaurar o valor do Cadastro de Perguntas' : 'Personalizar somente para este questionário'}
    >
      {overridden ? 'Restaurar' : 'Alterar'}
    </Button>
  </div>
);

// ── Helpers ────────────────────────────────────────────────────────────────────

function tipoLabel(tipo: TipoResposta) {
  return { SIM_NAO: 'Sim/Não', PERCENTUAL: 'Percentual', TEXTO_LIVRE: 'Texto', MULTIPLA_ESCOLHA: 'Múltipla' }[tipo] || tipo;
}

function tipoColor(tipo: TipoResposta) {
  return { SIM_NAO: 'success', PERCENTUAL: 'primary', TEXTO_LIVRE: 'secondary', MULTIPLA_ESCOLHA: 'warning' }[tipo] || 'secondary';
}

function buildDefaultCondicoes(tipo: TipoResposta): Condicao[] {
  if (tipo === 'SIM_NAO') {
    return [
      { resposta_valor: 'SIM', acao: 'PROXIMA_PRINCIPAL', ordem: 1, complementar_temp_id: null },
      { resposta_valor: 'NAO', acao: 'PROXIMA_PRINCIPAL', ordem: 1, complementar_temp_id: null },
    ];
  }
  return [
    { resposta_valor: 'RESPONDIDA', acao: 'PROXIMA_PRINCIPAL', ordem: 1, complementar_temp_id: null },
  ];
}

function catalogoSnapshotOf(p: Pergunta): CatalogoSnapshot {
  return {
    tipo_resposta: p.tipo_resposta,
    peso: p.peso ?? 100,
    checklist_id: p.checklist_id ?? null,
    permite_na: p.permite_na !== false,
  };
}

// Recomputa a árvore de renderização a partir das Condições: quem aponta (COMPLEMENTAR)
// para quem define o pai de cada pergunta. Perguntas complementares não alcançáveis a
// partir de uma Principal (destino removido/alterado) são descartadas em cascata.
function resyncTree(list: QItem[]): QItem[] {
  const byId = new Map(list.map(q => [q.temp_id, q]));
  const parentOf = new Map<string, string>();
  list.forEach(item => {
    item.condicoes.forEach(cond => {
      if (cond.acao === 'COMPLEMENTAR' && cond.complementar_temp_id) {
        parentOf.set(cond.complementar_temp_id, item.temp_id);
      }
    });
  });

  const visited = new Set<string>();
  const ordered: QItem[] = [];
  const walk = (item: QItem) => {
    if (visited.has(item.temp_id)) return;
    visited.add(item.temp_id);
    ordered.push(item);
    item.condicoes.forEach(cond => {
      if (cond.acao === 'COMPLEMENTAR' && cond.complementar_temp_id) {
        const child = byId.get(cond.complementar_temp_id);
        if (child) walk(child);
      }
    });
  };
  list.forEach(item => { if (item.nivel === 'principal') walk(item); });

  return ordered.map((item, idx) => ({
    ...item,
    parent_temp_id: parentOf.get(item.temp_id) ?? null,
    ordem: idx,
  }));
}

// ── Modal: como aplicar o Checklist (Do cadastro / Personalizar) ────────────────

interface ChecklistChoiceModalProps {
  show: boolean;
  onHide: () => void;
  onConfirm: (modo: 'cadastro' | 'personalizar') => void;
}

const ChecklistChoiceModal: React.FC<ChecklistChoiceModalProps> = ({ show, onHide, onConfirm }) => {
  const [modo, setModo] = useState<'cadastro' | 'personalizar'>('cadastro');
  useEffect(() => { if (show) setModo('cadastro'); }, [show]);
  return (
    <Modal show={show} onHide={onHide} centered size="sm">
      <Modal.Body>
        <div className="fw-semibold mb-1">Como deseja aplicar o Checklist para esta pergunta?</div>
        <div className="text-muted small mb-3">
          Escolha se o checklist padrão do cadastro deve ser usado, ou se prefere personalizar
          apenas para esta pergunta.
        </div>
        <div className="fw-semibold small mb-2">
          Usar configurações da pergunta ou personalizar para este questionário.
        </div>
        <div className="btn-group w-100" role="group">
          <Button variant={modo === 'personalizar' ? 'primary' : 'outline-primary'} onClick={() => setModo('personalizar')}>
            Personalizar
          </Button>
          <Button variant={modo === 'cadastro' ? 'primary' : 'outline-primary'} onClick={() => setModo('cadastro')}>
            Do cadastro
          </Button>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>Cancelar</Button>
        <Button variant="primary" onClick={() => onConfirm(modo)}>OK</Button>
      </Modal.Footer>
    </Modal>
  );
};

// ── Modal: personalizar os itens do checklist para esta pergunta ────────────────

interface ChecklistEditModalProps {
  show: boolean;
  perguntaText: string;
  pesoPergunta: number;
  itensIniciais: ChecklistItemPers[];
  onCancel: () => void;
  onSave: (itens: ChecklistItemPers[]) => void;
}

const ChecklistEditModal: React.FC<ChecklistEditModalProps> = ({
  show, perguntaText, pesoPergunta, itensIniciais, onCancel, onSave,
}) => {
  const [itens, setItens] = useState<ChecklistItemPers[]>([]);
  const [auto, setAuto] = useState(true);
  const [newDesc, setNewDesc] = useState('');
  const [newPeso, setNewPeso] = useState(0);
  const [newNivel, setNewNivel] = useState<NivelControle>('MANDATORIO');

  useEffect(() => {
    if (show) {
      setItens(itensIniciais.map(i => ({ ...i })));
      setAuto(true);
      setNewDesc('');
      setNewPeso(0);
      setNewNivel('MANDATORIO');
    }
  }, [show, itensIniciais]);

  const ativoSig = itens.map(i => (i.ativo ? '1' : '0')).join('');

  // Soma ao vivo, pros mesmos motivos do formulário de Perguntas: avisar assim que o
  // limite de 100% é ultrapassado, sem esperar o clique em "Salvar Alterações"
  // (aqui nem existia checagem nenhuma antes de salvar).
  // Number(...): percentual_peso pode vir como string do backend (coluna numérica do
  // Postgres) — sem essa coerção, "0 + '20.00'" vira concatenação de texto, não soma.
  const somaItensAtivos = itens.filter(i => i.ativo).reduce((s, i) => s + (Number(i.percentual_peso) || 0), 0);
  const somaTotalPeso = pesoPergunta + somaItensAtivos;
  const excedeLimitePeso = !auto && somaTotalPeso > 100;
  useEffect(() => {
    if (!auto) return;
    const ativos = itens.filter(i => i.ativo);
    if (ativos.length === 0) return;
    const pesoPorItem = Math.max(0, 100 - pesoPergunta) / ativos.length;
    setItens(prev => prev.map(i => (i.ativo ? { ...i, percentual_peso: Math.round(pesoPorItem * 100) / 100 } : i)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auto, pesoPergunta, itens.length, ativoSig]);

  const change = (idx: number, field: keyof ChecklistItemPers, value: any) => {
    setItens(prev => prev.map((it, i) => {
      if (i !== idx) return it;
      const u = { ...it, [field]: value };
      if (field === 'ativo' && value === false) u.percentual_peso = 0; // Não → sem peso
      return u;
    }));
  };
  const remove = (idx: number) => setItens(prev => prev.filter((_, i) => i !== idx));
  const add = () => {
    if (!newDesc.trim()) return;
    setItens(prev => [...prev, { descricao: newDesc.trim(), ativo: true, percentual_peso: newPeso, nivel_controle: newNivel }]);
    setNewDesc('');
    setNewPeso(0);
  };

  return (
    <Modal show={show} onHide={onCancel} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title className="fs-6">Personalizando Item de Checklist no Questionário</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="text-muted small mb-2">{perguntaText}</div>
        <Row className="g-2 mb-3 align-items-end">
          <Col md={5}>
            <Form.Label className="small">Item do Checklist</Form.Label>
            <Form.Control
              size="sm" value={newDesc}
              onChange={e => setNewDesc(e.target.value)}
              placeholder="Descrição do item..."
              onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); add(); } }}
            />
          </Col>
          <Col md={3}>
            <Form.Label className="small">Nível do Controle</Form.Label>
            <Form.Select size="sm" value={newNivel} onChange={e => setNewNivel(e.target.value as NivelControle)}>
              {NIVEL_CONTROLE_OPTIONS.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </Form.Select>
          </Col>
          <Col md={2}>
            <Form.Label className="small">Peso (%)</Form.Label>
            <InputGroup size="sm">
              <Form.Control type="number" min={0} max={100} step={0.01} value={newPeso}
                onChange={e => setNewPeso(parseFloat(e.target.value) || 0)} disabled={auto} />
              <InputGroup.Text>%</InputGroup.Text>
            </InputGroup>
          </Col>
          <Col md={2}>
            <Button size="sm" variant="outline-primary" className="w-100" onClick={add} disabled={!newDesc.trim()}>
              + Adicionar
            </Button>
          </Col>
        </Row>

        {itens.length > 0 ? (
          <Table size="sm" bordered className="mb-2">
            <thead className="table-light">
              <tr>
                <th colSpan={2} className="text-center">Nível de Maturidade</th>
                <th rowSpan={2} className="align-middle" style={{ width: 160 }}>Nível do Controle</th>
                <th rowSpan={2} className="align-middle">Item</th>
                <th rowSpan={2} className="align-middle" style={{ width: 120 }}>Peso (%)</th>
                <th rowSpan={2} style={{ width: 50 }}></th>
              </tr>
              <tr>
                <th className="text-center" style={{ width: 55 }}>Sim</th>
                <th className="text-center" style={{ width: 55 }}>Não</th>
              </tr>
            </thead>
            <tbody>
              {itens.map((it, idx) => (
                <tr key={idx}>
                  <td className="text-center align-middle">
                    <Form.Check type="radio" name={`pers-${idx}`} checked={it.ativo} onChange={() => change(idx, 'ativo', true)} />
                  </td>
                  <td className="text-center align-middle">
                    <Form.Check type="radio" name={`pers-${idx}`} checked={!it.ativo} onChange={() => change(idx, 'ativo', false)} />
                  </td>
                  <td>
                    <Form.Select
                      size="sm"
                      value={it.nivel_controle || 'MANDATORIO'}
                      onChange={e => change(idx, 'nivel_controle', e.target.value as NivelControle)}
                    >
                      {NIVEL_CONTROLE_OPTIONS.map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </Form.Select>
                  </td>
                  <td>
                    <Form.Control size="sm" value={it.descricao} onChange={e => change(idx, 'descricao', e.target.value)} />
                  </td>
                  <td>
                    <InputGroup size="sm">
                      <Form.Control
                        type="number" min={0} max={100} step={0.01}
                        value={it.ativo ? it.percentual_peso : ''}
                        onChange={e => change(idx, 'percentual_peso', parseFloat(e.target.value) || 0)}
                        disabled={auto || !it.ativo}
                      />
                      <InputGroup.Text>%</InputGroup.Text>
                    </InputGroup>
                  </td>
                  <td className="text-center align-middle">
                    <Button size="sm" variant="outline-danger" onClick={() => remove(idx)}>×</Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        ) : (
          <p className="text-muted small">Nenhum item. Adicione itens acima.</p>
        )}

        <div className="d-flex justify-content-end">
          <Form.Check type="switch" label="Calcular automaticamente" checked={auto} onChange={e => setAuto(e.target.checked)} />
        </div>
        <div className={`small mt-1 ${excedeLimitePeso ? 'text-danger fw-semibold' : 'text-muted'}`}>
          {auto
            ? `Peso da pergunta: ${pesoPergunta}% + itens dividem os ${Math.max(0, 100 - pesoPergunta)}% restantes igualmente entre os que estiverem "Sim".`
            : `Informe o peso de cada item manualmente. Soma atual: Peso da pergunta (${pesoPergunta}%) + itens marcados "Sim" (${somaItensAtivos.toFixed(2)}%) = ${somaTotalPeso.toFixed(2)}%${excedeLimitePeso ? ' — excede 100%, reduza algum valor antes de salvar.' : '.'}`}
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onCancel}>Cancelar</Button>
        <Button variant="primary" onClick={() => onSave(itens)} disabled={excedeLimitePeso}>Salvar Alterações</Button>
      </Modal.Footer>
    </Modal>
  );
};

// ── Main component ─────────────────────────────────────────────────────────────

const QuestionnaireRegistration: React.FC = () => {
  const { id } = useParams<{ id?: string }>();
  const navigate = useNavigate();
  const { empresaId, tipoEmpresa } = useInterfaceStore();

  const tipoPessoa: string = (() => {
    try { return JSON.parse(localStorage.getItem('usuarioData') || '{}').tipo_pessoa || ''; }
    catch { return ''; }
  })();
  const canWrite = tipoEmpresa === '4Comply' || ['Administrador', 'DPO'].includes(tipoPessoa);
  const isEditing = !!id;

  // ── Header state ──────────────────────────────────────────────────────────
  const [nome, setNome] = useState('');
  const [descricao, setDescricao] = useState('');
  const [frameworkId, setFrameworkId] = useState<number | null>(null);
  const [frameworkNome, setFrameworkNome] = useState('');
  // Escopo do Questionário (Cadastro Auxiliar escopo_questionario) — relaciona ao fornecedor.
  const [escopo, setEscopo] = useState('');
  const [escopoItems, setEscopoItems] = useState<{ id: number; nome: string }[]>([]);
  const [isModelo, setIsModelo] = useState(false);
  // Não tem UI própria pra alterar aqui (só a lista de questionários fecha um), mas precisa
  // ser carregado pra desabilitar o envio de um questionário já Fechado.
  const [isFechado, setIsFechado] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // ── Enviar Questionário aos Fornecedores ──────────────────────────────────
  const [showEnviarModal, setShowEnviarModal] = useState(false);
  const [carregandoFornecedores, setCarregandoFornecedores] = useState(false);
  const [fornecedoresEscopo, setFornecedoresEscopo] = useState<FornecedorEscopoRow[]>([]);
  const [selecaoEnvio, setSelecaoEnvio] = useState<Record<number, boolean>>({});
  const [prazoAlvoEnvio, setPrazoAlvoEnvio] = useState('');
  const [progressoEnvio, setProgressoEnvio] = useState<ProgressoEnvioItem[] | null>(null);
  const [paginaProgresso, setPaginaProgresso] = useState(0);

  // ── Questions state ───────────────────────────────────────────────────────
  const [questions, setQuestions] = useState<QItem[]>([]);
  const [focusedTempId, setFocusedTempId] = useState<string | null>(null);

  // ── Reference data ────────────────────────────────────────────────────────
  const [frameworks, setFrameworks] = useState<Framework[]>([]);
  const [allPerguntas, setAllPerguntas] = useState<Pergunta[]>([]);
  const [filteredPerguntas, setFilteredPerguntas] = useState<Pergunta[]>([]);

  // ── Picker modal (adicionar Pergunta Principal) ──────────────────────────────
  const [showPicker, setShowPicker] = useState(false);

  // ── Checklist: escolha (cadastro/personalizar) e edição de itens ──────────
  const [checklistChoice, setChecklistChoice] = useState<{ tempId: string } | null>(null);
  const [checklistEdit, setChecklistEdit] = useState<{ tempId: string; itens: ChecklistItemPers[] } | null>(null);

  // ── Clone modal ───────────────────────────────────────────────────────────
  const [showClone, setShowClone] = useState(false);
  const [cloning, setCloning] = useState(false);

  // ── Framework popup modal ──────────────────────────────────────────────────
  const [showFrameworkModal, setShowFrameworkModal] = useState(false);
  const [frameworkSearch, setFrameworkSearch] = useState('');

  // ── Load reference data ───────────────────────────────────────────────────
  useEffect(() => {
    const load = async () => {
      const [fwRes, qRes, escRes] = await Promise.all([
        api.getFrameworks(),
        api.getQuestions(),
        api.getCadastrosAuxiliares('escopo_questionario', empresaId ?? undefined),
      ]);
      if (fwRes.success) setFrameworks(fwRes.data || []);
      if (qRes.success) { setAllPerguntas(qRes.data || []); setFilteredPerguntas(qRes.data || []); }
      if (escRes.success) {
        setEscopoItems(((escRes.data as any[]) || [])
          .filter(r => r.ativo !== false)
          .map(r => ({ id: r.id, nome: r.nome })));
      }
    };
    load();
  }, [empresaId]);

  // ── Filter perguntas by framework ──────────────────────────────────────────
  const loadPerguntasByFramework = useCallback(async (fwId: number | null) => {
    if (!fwId) { setFilteredPerguntas(allPerguntas); return; }
    const res = await api.getPerguntasByFramework(fwId);
    setFilteredPerguntas((res.success && Array.isArray(res.data) && res.data.length > 0) ? res.data : allPerguntas);
  }, [allPerguntas]);

  // Refiltra as perguntas disponíveis sempre que o framework muda
  useEffect(() => {
    loadPerguntasByFramework(frameworkId);
  }, [frameworkId, loadPerguntasByFramework]);

  // ── Load questionnaire for editing ───────────────────────────────────────
  useEffect(() => {
    if (!id || allPerguntas.length === 0) return;
    setLoading(true);
    api.getQuestionnaire(parseInt(id)).then(res => {
      if (!res.success || !res.data) { setError('Questionário não encontrado.'); return; }
      const q = res.data;
      setNome(q.nome || '');
      setDescricao(q.descricao || '');
      setIsModelo(!!q.is_modelo);
      setIsFechado(!!q.is_fechado);
      setFrameworkId(q.framework_id || null);
      setFrameworkNome(q.framework || '');
      setEscopo(q.escopo || '');

      // Rebuild QItems from perguntas
      if (Array.isArray(q.perguntas) && q.perguntas.length > 0) {
        // First pass: create items with temp IDs, map qp_id → temp_id
        const qpToTempId: Record<number, string> = {};
        const items: QItem[] = q.perguntas.map((p: any) => {
          const tempId = newTempId();
          qpToTempId[p.qp_id] = tempId;
          const catalogPergunta = allPerguntas.find(cp => cp.id === p.pergunta_id);
          const catalogo = catalogPergunta ? catalogoSnapshotOf(catalogPergunta) : {
            tipo_resposta: p.tipo_resposta, peso: 100, checklist_id: null, permite_na: true,
          };
          const peso = p.peso ?? 100;
          const checklist_id = p.checklist_id ?? null;
          const permite_na = p.permite_na ?? true;
          const tipo_resposta = p.tipo_resposta;
          const ignorar_equivalencia = !!p.ignorar_equivalencia;
          return {
            temp_id: tempId,
            pergunta_id: p.pergunta_id,
            pergunta_text: p.pergunta_text,
            tipo_resposta,
            peso,
            checklist_id,
            permite_na,
            ignorar_equivalencia,
            nivel: p.nivel || 'principal',
            parent_temp_id: null,
            ordem: p.ordem || 0,
            condicoes: [],
            catalogo,
            checklist_personalizado: !!p.checklist_personalizado,
            checklist_itens: p.checklist_personalizado ? (p.checklist_itens || []) : undefined,
            overrides: {
              tipo_resposta: tipo_resposta !== catalogo.tipo_resposta,
              peso: peso !== catalogo.peso,
              checklist_id: checklist_id !== catalogo.checklist_id,
              permite_na: permite_na !== catalogo.permite_na,
              ignorar_equivalencia,
            },
          } as QItem;
        });

        // Second pass: resolve conditions (qp_id references → temp_ids)
        q.perguntas.forEach((p: any, idx: number) => {
          if (Array.isArray(p.condicoes)) {
            items[idx].condicoes = p.condicoes.map((c: any) => ({
              resposta_valor: c.resposta_valor,
              acao: c.acao,
              ordem: c.ordem || 0,
              complementar_temp_id: c.pergunta_complementar_qp_id
                ? (qpToTempId[c.pergunta_complementar_qp_id] || null)
                : null,
            }));
          } else {
            items[idx].condicoes = buildDefaultCondicoes(items[idx].tipo_resposta);
          }
        });

        setQuestions(resyncTree(items));
      }
    }).catch(() => setError('Erro ao carregar questionário.'))
      .finally(() => setLoading(false));
  }, [id, allPerguntas]);

  // ── Question management ───────────────────────────────────────────────────

  const addQuestion = (pergunta: Pergunta) => {
    const catalogo = catalogoSnapshotOf(pergunta);
    const newItem: QItem = {
      temp_id: newTempId(),
      pergunta_id: pergunta.id,
      pergunta_text: pergunta.pergunta,
      tipo_resposta: catalogo.tipo_resposta,
      peso: catalogo.peso,
      checklist_id: catalogo.checklist_id,
      permite_na: catalogo.permite_na,
      ignorar_equivalencia: false,
      nivel: 'principal',
      parent_temp_id: null,
      ordem: questions.length,
      condicoes: buildDefaultCondicoes(catalogo.tipo_resposta),
      catalogo,
      overrides: noOverrides(),
    };
    setQuestions(prev => resyncTree([...prev, newItem]));
    setShowPicker(false);
    setFocusedTempId(newItem.temp_id);
  };

  const removeQuestion = (tempId: string) => {
    setQuestions(prev => {
      // Also remove all descendants
      const toRemove = new Set<string>();
      const collectDescendants = (tid: string) => {
        toRemove.add(tid);
        prev.filter(q => q.parent_temp_id === tid).forEach(c => collectDescendants(c.temp_id));
      };
      collectDescendants(tempId);
      const next = prev
        .filter(q => !toRemove.has(q.temp_id))
        .map(q => ({
          ...q,
          condicoes: q.condicoes.map(c => ({
            ...c,
            complementar_temp_id: c.complementar_temp_id && toRemove.has(c.complementar_temp_id)
              ? null
              : c.complementar_temp_id,
          })),
        }));
      return resyncTree(next);
    });
    setFocusedTempId(prev => (prev === tempId ? null : prev));
  };

  const updateQuestion = (tempId: string, updates: Partial<QItem>) => {
    setQuestions(prev => prev.map(q => q.temp_id === tempId ? { ...q, ...updates } : q));
  };

  // Alterna um campo individual entre "Do cadastro" (valor do catálogo, travado)
  // e "Alterar" (editável, personalizado apenas para este questionário).
  const toggleOverride = (tempId: string, field: keyof Overrides, next: boolean) => {
    setQuestions(prev => prev.map(q => {
      if (q.temp_id !== tempId) return q;
      const overrides = { ...q.overrides, [field]: next };
      if (field === 'ignorar_equivalencia') {
        // Não tem valor de cadastro correspondente: o "overridden" já É o valor do campo.
        return { ...q, overrides, ignorar_equivalencia: next };
      }
      if (next) return { ...q, overrides };
      // Voltando para "Do cadastro": restaura o valor do catálogo
      const restored: Partial<QItem> = { overrides };
      if (field === 'tipo_resposta') {
        restored.tipo_resposta = q.catalogo.tipo_resposta;
        restored.condicoes = buildDefaultCondicoes(q.catalogo.tipo_resposta);
      }
      if (field === 'peso') restored.peso = q.catalogo.peso;
      if (field === 'checklist_id') {
        restored.checklist_id = q.catalogo.checklist_id;
        restored.checklist_personalizado = false;
        restored.checklist_itens = undefined;
      }
      if (field === 'permite_na') restored.permite_na = q.catalogo.permite_na;
      return { ...q, ...restored };
    }));
  };

  const restoreAllFromCatalogo = (tempId: string) => {
    setQuestions(prev => prev.map(q => {
      if (q.temp_id !== tempId) return q;
      return {
        ...q,
        tipo_resposta: q.catalogo.tipo_resposta,
        peso: q.catalogo.peso,
        checklist_id: q.catalogo.checklist_id,
        permite_na: q.catalogo.permite_na,
        ignorar_equivalencia: false,
        checklist_personalizado: false,
        checklist_itens: undefined,
        condicoes: buildDefaultCondicoes(q.catalogo.tipo_resposta),
        overrides: noOverrides(),
      };
    }));
  };

  // ── Checklist: ligar (com escolha) / personalizar ────────────────────────
  // Ao ligar o Checklist (em modo "Alterar"), abre a escolha Do cadastro/Personalizar.
  const handleChecklistToggle = (tempId: string, ligar: boolean) => {
    if (ligar) {
      setChecklistChoice({ tempId });
    } else {
      updateQuestion(tempId, {
        checklist_id: null,
        checklist_personalizado: false,
        checklist_itens: undefined,
        overrides: { ...(questions.find(q => q.temp_id === tempId)?.overrides || noOverrides()), checklist_id: true },
      });
    }
  };

  const handleChecklistChoice = async (modo: 'cadastro' | 'personalizar') => {
    const tempId = checklistChoice?.tempId;
    if (!tempId) return;
    const q = questions.find(x => x.temp_id === tempId);
    if (!q) { setChecklistChoice(null); return; }

    if (modo === 'cadastro') {
      // Usa o checklist padrão do cadastro da pergunta.
      updateQuestion(tempId, {
        checklist_id: q.catalogo.checklist_id ?? 0,
        checklist_personalizado: false,
        checklist_itens: undefined,
        overrides: { ...q.overrides, checklist_id: true },
      });
      setChecklistChoice(null);
      return;
    }

    // Personalizar: carrega os itens do checklist do cadastro (se houver) para editar.
    let itensBase: ChecklistItemPers[] = [];
    if (q.catalogo.checklist_id) {
      try {
        const res = await api.getChecklistCompleto(q.catalogo.checklist_id);
        if (res.success && res.data) {
          const requisitos = (res.data as any).requisitos || [];
          itensBase = requisitos.flatMap((r: any) => (r.itens || []).map((it: any) => ({
            descricao: it.descricao_item || it.descricao || '',
            ativo: it.ativo !== false,
            percentual_peso: it.percentual_peso || 0,
            nivel_controle: it.nivel_controle || 'MANDATORIO',
          })));
        }
      } catch { /* segue com lista vazia */ }
    }
    setChecklistChoice(null);
    setChecklistEdit({ tempId, itens: itensBase });
  };

  const handleChecklistEditSave = (itens: ChecklistItemPers[]) => {
    const tempId = checklistEdit?.tempId;
    if (!tempId) return;
    setQuestions(prev => prev.map(q => q.temp_id !== tempId ? q : {
      ...q,
      checklist_id: CHECKLIST_PERSONALIZADO_ID, // sentinel "ligado"; backend cria o real
      checklist_personalizado: true,
      checklist_itens: itens,
      overrides: { ...q.overrides, checklist_id: true },
    }));
    setChecklistEdit(null);
  };

  const handleTipoRespostaChange = (tempId: string, novoTipo: TipoResposta) => {
    setQuestions(prev => prev.map(q => q.temp_id !== tempId ? q : {
      ...q,
      tipo_resposta: novoTipo,
      condicoes: buildDefaultCondicoes(novoTipo),
      overrides: { ...q.overrides, tipo_resposta: true },
    }));
  };

  const updateCondicao = (tempId: string, resposta_valor: string, updates: Partial<Condicao>) => {
    setQuestions(prev => {
      const next = prev.map(q => {
        if (q.temp_id !== tempId) return q;
        return {
          ...q,
          condicoes: q.condicoes.map(c =>
            c.resposta_valor === resposta_valor ? { ...c, ...updates } : c
          ),
        };
      });
      return resyncTree(next);
    });
  };

  // Chamado pelo Select "Destino" de uma Condição: cria (se necessário) e posiciona
  // automaticamente o card da Pergunta Complementar logo abaixo do card pai.
  const handleDestinoSelect = (parentTempId: string, resposta_valor: string, perguntaIdStr: string) => {
    setQuestions(prev => {
      const parent = prev.find(q => q.temp_id === parentTempId);
      const cond = parent?.condicoes.find(c => c.resposta_valor === resposta_valor);
      if (!parent || !cond) return prev;

      if (!perguntaIdStr) {
        return resyncTree(prev.map(q => q.temp_id !== parentTempId ? q : {
          ...q,
          condicoes: q.condicoes.map(c => c.resposta_valor === resposta_valor ? { ...c, complementar_temp_id: null } : c),
        }));
      }

      const perguntaId = Number(perguntaIdStr);
      const currentChild = prev.find(q => q.temp_id === cond.complementar_temp_id);
      if (currentChild && currentChild.pergunta_id === perguntaId) return prev; // sem alteração

      const catalogPergunta = filteredPerguntas.find(p => p.id === perguntaId) || allPerguntas.find(p => p.id === perguntaId);
      if (!catalogPergunta) return prev;

      const catalogo = catalogoSnapshotOf(catalogPergunta);
      const newItem: QItem = {
        temp_id: newTempId(),
        pergunta_id: catalogPergunta.id,
        pergunta_text: catalogPergunta.pergunta,
        tipo_resposta: catalogo.tipo_resposta,
        peso: catalogo.peso,
        checklist_id: catalogo.checklist_id,
        permite_na: catalogo.permite_na,
        ignorar_equivalencia: false,
        nivel: 'complementar',
        parent_temp_id: parentTempId,
        ordem: 0,
        condicoes: buildDefaultCondicoes(catalogo.tipo_resposta),
        catalogo,
        overrides: noOverrides(),
      };

      const next = prev.map(q => q.temp_id !== parentTempId ? q : {
        ...q,
        condicoes: q.condicoes.map(c => c.resposta_valor === resposta_valor ? { ...c, complementar_temp_id: newItem.temp_id } : c),
      });
      return resyncTree([...next, newItem]);
    });
  };

  // ── Save ──────────────────────────────────────────────────────────────────

  const handleSave = async () => {
    if (!nome.trim()) { notify.warning('Nome é obrigatório.'); return; }
    if (!empresaId) { notify.warning('Selecione uma empresa ativa. Verifique se você selecionou uma empresa no menu inicial.'); return; }

    setSaving(true);
    try {
      const payload = {
        nome: nome.trim(),
        descricao,
        empresa_id: empresaId,
        framework_id: frameworkId,
        escopo: escopo || null,
        is_modelo: isModelo,
        perguntas: questions.map(q => ({
          temp_id: q.temp_id,
          pergunta_id: q.pergunta_id,
          tipo_resposta: q.tipo_resposta,
          peso: q.peso,
          checklist_id: q.checklist_personalizado ? null : q.checklist_id,
          checklist_personalizado: !!q.checklist_personalizado,
          checklist_itens: q.checklist_personalizado ? (q.checklist_itens || []) : undefined,
          permite_na: q.permite_na,
          ignorar_equivalencia: q.ignorar_equivalencia,
          nivel: q.nivel,
          parent_temp_id: q.parent_temp_id,
          ordem: q.ordem,
          condicoes: q.condicoes,
        })),
      };

      let res;
      if (isEditing) {
        res = await api.updateQuestionnaire(parseInt(id!), payload);
      } else {
        res = await api.createQuestionnaire(payload);
      }

      if (res.success) {
        notify.success(isEditing ? 'Questionário atualizado!' : 'Questionário criado!');
        navigate('/app/questionarios/list');
      } else {
        const msg = (res.data as any)?.error || res.error || 'Erro ao salvar.';
        setError(msg);
        notify.error(msg);
      }
    } catch (err: any) {
      notify.error('Erro ao salvar questionário: ' + (err?.message || err));
    } finally {
      setSaving(false);
    }
  };

  // ── Clone ─────────────────────────────────────────────────────────────────

  const handleClone = async (opcoes: CloneOptions) => {
    if (!empresaId) return;
    setCloning(true);
    try {
      const res = await api.cloneQuestionnaire(parseInt(id!), { ...opcoes, empresa_id: empresaId });
      if (res.success) {
        notify.success('Questionário clonado com sucesso!');
        setShowClone(false);
        navigate(`/app/questionarios/editar/${res.data.id}`);
      } else {
        notify.error('Erro ao clonar.');
      }
    } catch {
      notify.error('Erro ao clonar.');
    } finally {
      setCloning(false);
    }
  };

  // ── Enviar Questionário aos Fornecedores ──────────────────────────────────

  const podeEnviarQuestionario = isEditing && !isModelo && !isFechado && escopo.trim() !== '';

  const abrirEnviarModal = async () => {
    setShowEnviarModal(true);
    setCarregandoFornecedores(true);
    setProgressoEnvio(null);
    try {
      const res = await api.getFornecedoresEscopoQuestionario(parseInt(id!));
      const linhas = (res.success && Array.isArray(res.data)) ? (res.data as FornecedorEscopoRow[]) : [];
      setFornecedoresEscopo(linhas);
      // Desmarcado por padrão, conforme solicitado.
      setSelecaoEnvio(Object.fromEntries(linhas.map(l => [l.contrato_escopo_id, false])));
      setPrazoAlvoEnvio('');
    } catch {
      notify.error('Erro ao carregar fornecedores deste escopo.');
      setFornecedoresEscopo([]);
    } finally {
      setCarregandoFornecedores(false);
    }
  };

  const selecionarTodosEnvio = (marcar: boolean) => {
    setSelecaoEnvio(Object.fromEntries(fornecedoresEscopo.map(l => [l.contrato_escopo_id, marcar])));
  };

  // Confirma a seleção: fecha a tela de escolha e passa pra tela de progresso, processando
  // os envios um de cada vez (reaproveita a mesma rota já usada no envio individual do lado
  // do Fornecedor) — assim cada item vai virando "Enviado"/"Erro" ao vivo, em vez de um
  // spinner único até o fim.
  const confirmarEnvioFornecedores = async () => {
    const selecionados = fornecedoresEscopo.filter(l => selecaoEnvio[l.contrato_escopo_id]);
    if (selecionados.length === 0) {
      notify.warning('Selecione ao menos um fornecedor.');
      return;
    }
    setPaginaProgresso(0);
    setProgressoEnvio(selecionados.map(l => ({
      contrato_escopo_id: l.contrato_escopo_id, fornecedor_nome: l.fornecedor_nome, status: 'aguardando',
    })));

    for (const linha of selecionados) {
      setProgressoEnvio(prev => (prev || []).map(p =>
        p.contrato_escopo_id === linha.contrato_escopo_id ? { ...p, status: 'enviando' } : p
      ));
      let ok = false;
      try {
        const res = await api.enviarQuestionarioEscopo(linha.contrato_escopo_id, {
          questionario_id: parseInt(id!),
          data_alvo_resposta: prazoAlvoEnvio || undefined,
        });
        ok = !!res.success;
      } catch {
        ok = false;
      }
      setProgressoEnvio(prev => (prev || []).map(p =>
        p.contrato_escopo_id === linha.contrato_escopo_id ? { ...p, status: ok ? 'enviado' : 'erro' } : p
      ));
    }
  };

  const fecharEnviarModal = () => {
    setShowEnviarModal(false);
    setProgressoEnvio(null);
  };

  // Mantém a paginação (3 por tela) sempre acompanhando o item em andamento, avançando
  // sozinha conforme os envios anteriores terminam.
  useEffect(() => {
    if (!progressoEnvio) return;
    const idxAtivo = progressoEnvio.findIndex(p => p.status === 'enviando' || p.status === 'aguardando');
    const idx = idxAtivo === -1 ? progressoEnvio.length - 1 : idxAtivo;
    setPaginaProgresso(Math.floor(Math.max(0, idx) / 3));
  }, [progressoEnvio]);

  const progressoConcluidos = progressoEnvio ? progressoEnvio.filter(p => p.status === 'enviado' || p.status === 'erro').length : 0;
  const progressoFinalizado = !!progressoEnvio && progressoConcluidos === progressoEnvio.length;
  const totalPaginasProgresso = progressoEnvio ? Math.ceil(progressoEnvio.length / 3) : 0;

  // ── Render helpers ────────────────────────────────────────────────────────

  // Perguntas já usadas neste questionário (Principal ou Complementar) — não faz sentido
  // oferecer de novo, nem na tela de "+ Adicionar Pergunta Principal" nem no destino de
  // uma Complementar, evitando o mesmo item aparecer duas vezes na Visualização.
  const perguntaIdsUsadas = new Set(questions.map(q => q.pergunta_id));

  const renderCondicoesRow = (q: QItem) => {
    const childByCond = (complementarTempId: string | null) =>
      complementarTempId ? questions.find(x => x.temp_id === complementarTempId) : undefined;

    return q.condicoes.map(cond => {
      // Destino disponível: catálogo de perguntas, excluindo a própria pergunta e as já
      // usadas — mas mantendo visível a que já está selecionada aqui (senão o Select fica
      // sem opção correspondente ao valor atual).
      const currentChild = childByCond(cond.complementar_temp_id);
      const destinoOptions = filteredPerguntas.filter(p =>
        p.id !== q.pergunta_id && (p.id === currentChild?.pergunta_id || !perguntaIdsUsadas.has(p.id))
      );
      return (
      <tr key={cond.resposta_valor}>
        <td>
          <Badge bg={cond.resposta_valor === 'SIM' ? 'success' : cond.resposta_valor === 'NAO' ? 'danger' : 'secondary'}>
            {cond.resposta_valor}
          </Badge>
        </td>
        <td>
          <Form.Select
            size="sm"
            value={cond.acao}
            onChange={e => updateCondicao(q.temp_id, cond.resposta_valor, {
              acao: e.target.value as Acao,
              complementar_temp_id: e.target.value !== 'COMPLEMENTAR' ? null : cond.complementar_temp_id,
            })}
            disabled={!canWrite}
          >
            <option value="COMPLEMENTAR">Ir para Pergunta Complementar</option>
            <option value="PROXIMA_PRINCIPAL">Ir para Próxima Principal</option>
            <option value="FIM">Fim do Questionário</option>
          </Form.Select>
        </td>
        <td>
          {cond.acao === 'COMPLEMENTAR' && (
            <Form.Select
              size="sm"
              value={String(childByCond(cond.complementar_temp_id)?.pergunta_id ?? '')}
              onChange={e => handleDestinoSelect(q.temp_id, cond.resposta_valor, e.target.value)}
              disabled={!canWrite}
            >
              <option value="">Selecione a pergunta destino...</option>
              {destinoOptions.length === 0 ? (
                <option disabled value="">Nenhuma pergunta disponível no catálogo</option>
              ) : (
                destinoOptions.map(p => (
                  <option key={p.id} value={p.id}>{p.pergunta.substring(0, 70)}</option>
                ))
              )}
            </Form.Select>
          )}
        </td>
      </tr>
      );
    });
  };

  const renderQuestionCard = (q: QItem, depth = 0) => {
    const anyOverridden = q.overrides.tipo_resposta || q.overrides.peso || q.overrides.checklist_id
      || q.overrides.permite_na || q.overrides.ignorar_equivalencia;
    return (
      <Card
        key={q.temp_id}
        className="mb-3"
        style={{ marginLeft: depth * 24, borderLeft: depth > 0 ? '3px solid #6c757d' : undefined }}
      >
        <Card.Header className="d-flex align-items-center gap-2 py-2">
          <Badge bg={q.nivel === 'principal' ? 'primary' : 'secondary'}>
            {q.nivel === 'principal' ? 'Principal' : 'Complementar'}
          </Badge>
          <span className="text-truncate flex-grow-1" style={{ fontSize: '0.9rem', fontWeight: 500 }}>
            {q.pergunta_text}
          </span>
          <Badge bg={tipoColor(q.tipo_resposta)}>{tipoLabel(q.tipo_resposta)}</Badge>
          {canWrite && (
            <Button variant="outline-danger" size="sm" onClick={() => removeQuestion(q.temp_id)}>×</Button>
          )}
        </Card.Header>
        <Card.Body className="py-2">
          <Row className="g-2 align-items-center mb-2">
            <Col xs="auto" className="d-flex align-items-center">
              <Form.Label className="mb-0 me-1">Peso:</Form.Label>
              <Form.Control
                type="number" min={0} max={100} size="sm"
                style={{ width: 80, display: 'inline-block' }}
                value={q.peso}
                onChange={e => updateQuestion(q.temp_id, {
                  peso: Math.min(100, Math.max(0, Number(e.target.value))),
                  overrides: { ...q.overrides, peso: true },
                })}
                disabled={!canWrite || !q.overrides.peso}
              />
              <CadastroToggle
                overridden={q.overrides.peso}
                disabled={!canWrite}
                onToggle={next => toggleOverride(q.temp_id, 'peso', next)}
              />
            </Col>
            <Col xs="auto" className="d-flex align-items-center">
              <Form.Check
                type="switch"
                label={q.checklist_personalizado ? 'Checklist (personalizado)' : 'Checklist'}
                checked={q.checklist_id !== null}
                onChange={e => handleChecklistToggle(q.temp_id, e.target.checked)}
                disabled={!canWrite || !q.overrides.checklist_id}
              />
              {q.checklist_personalizado && q.checklist_id !== null && (
                <Button
                  variant="link" size="sm" className="p-0 ms-1 text-decoration-none"
                  onClick={() => setChecklistEdit({ tempId: q.temp_id, itens: q.checklist_itens || [] })}
                  disabled={!canWrite}
                >
                  editar itens
                </Button>
              )}
              <CadastroToggle
                overridden={q.overrides.checklist_id}
                disabled={!canWrite}
                onToggle={next => toggleOverride(q.temp_id, 'checklist_id', next)}
              />
            </Col>
            <Col xs="auto" className="d-flex align-items-center">
              <Form.Label className="mb-0 me-1">Tipo de resposta:</Form.Label>
              <Form.Select
                size="sm"
                style={{ width: 130, display: 'inline-block' }}
                value={q.tipo_resposta}
                onChange={e => handleTipoRespostaChange(q.temp_id, e.target.value as TipoResposta)}
                disabled={!canWrite || !q.overrides.tipo_resposta}
              >
                <option value="SIM_NAO">Sim/Não</option>
                <option value="PERCENTUAL">Barra %</option>
              </Form.Select>
              <CadastroToggle
                overridden={q.overrides.tipo_resposta}
                disabled={!canWrite}
                onToggle={next => toggleOverride(q.temp_id, 'tipo_resposta', next)}
              />
            </Col>
            <Col xs="auto" className="d-flex align-items-center">
              <Form.Check
                type="switch"
                label="N/A (Não aplica)"
                checked={q.permite_na}
                onChange={e => updateQuestion(q.temp_id, {
                  permite_na: e.target.checked,
                  overrides: { ...q.overrides, permite_na: true },
                })}
                disabled={!canWrite || !q.overrides.permite_na}
              />
              <CadastroToggle
                overridden={q.overrides.permite_na}
                disabled={!canWrite}
                onToggle={next => toggleOverride(q.temp_id, 'permite_na', next)}
              />
            </Col>
            <Col xs="auto" className="d-flex align-items-center">
              {/* Sem valor de cadastro correspondente: o switch e os botões controlam o
                  mesmo estado (ligado = ignora propagação por equivalência só aqui). */}
              <Form.Check
                type="switch"
                label="Ignorar relação de equivalência"
                checked={q.ignorar_equivalencia}
                onChange={e => toggleOverride(q.temp_id, 'ignorar_equivalencia', e.target.checked)}
                disabled={!canWrite}
              />
              <CadastroToggle
                overridden={q.overrides.ignorar_equivalencia}
                disabled={!canWrite}
                onToggle={next => toggleOverride(q.temp_id, 'ignorar_equivalencia', next)}
              />
            </Col>
          </Row>

          {anyOverridden && (
            <div className="text-warning small mb-2">
              Sobrescrevendo o valor do cadastro para esta pergunta.{' '}
              <Button
                variant="link"
                size="sm"
                className="p-0 align-baseline"
                onClick={() => restoreAllFromCatalogo(q.temp_id)}
                disabled={!canWrite}
              >
                Restaurar valor do cadastro para Todos os atributos
              </Button>
            </div>
          )}

          {/* Conditions */}
          <div style={{ fontSize: '0.85rem' }}>
            <strong>Condições:</strong>
            <Table size="sm" bordered className="mt-1 mb-0">
              <thead>
                <tr>
                  <th style={{ width: 80 }}>Resposta</th>
                  <th>Ação</th>
                  <th>Destino</th>
                </tr>
              </thead>
              <tbody>{renderCondicoesRow(q)}</tbody>
            </Table>
          </div>
        </Card.Body>
      </Card>
    );
  };

  // `questions` já vem ordenado (pai seguido de seus complementares, recursivamente)
  // por resyncTree(); aqui só calculamos a profundidade de cada card para indentação.
  const questionsById = new Map(questions.map(q => [q.temp_id, q]));
  const depthOf = (q: QItem): number => {
    let depth = 0;
    let cur: QItem | undefined = q;
    const seen = new Set<string>();
    while (cur?.parent_temp_id && !seen.has(cur.temp_id)) {
      seen.add(cur.temp_id);
      cur = questionsById.get(cur.parent_temp_id);
      depth++;
    }
    return depth;
  };

  // Um "bloco" é uma Principal + todas as suas Complementares — como resyncTree()
  // ordena a lista em profundidade (pai seguido de seus descendentes), o bloco é o
  // trecho contíguo a partir da Principal até a próxima pergunta de nível Principal.
  const getBlockItems = (rootTempId: string): QItem[] => {
    const startIdx = questions.findIndex(q => q.temp_id === rootTempId);
    if (startIdx === -1) return [];
    const block = [questions[startIdx]];
    for (let i = startIdx + 1; i < questions.length; i++) {
      if (questions[i].nivel === 'principal') break;
      block.push(questions[i]);
    }
    return block;
  };

  const renderCollapsedBlockRow = (p: QItem) => (
    <div
      key={p.temp_id}
      className="d-flex align-items-center gap-2 p-2 border rounded mb-2 bg-light"
      style={{ cursor: 'pointer' }}
      onClick={() => setFocusedTempId(p.temp_id)}
    >
      <Badge bg="primary">Principal</Badge>
      <span className="text-truncate flex-grow-1 small">{p.pergunta_text}</span>
      <Button
        variant="outline-secondary"
        size="sm"
        onClick={e => { e.stopPropagation(); setFocusedTempId(p.temp_id); }}
      >
        Editar bloco
      </Button>
    </div>
  );

  const renderPreviewItem = (q: QItem) => {
    const depth = depthOf(q);
    const parent = q.parent_temp_id ? questionsById.get(q.parent_temp_id) : undefined;
    const cond = parent?.condicoes.find(c => c.complementar_temp_id === q.temp_id);
    return (
      <div
        key={q.temp_id}
        className="d-flex align-items-center gap-2 py-1"
        style={{
          marginLeft: depth * 24,
          paddingLeft: depth > 0 ? 10 : 0,
          borderLeft: depth > 0 ? '2px dashed #adb5bd' : undefined,
        }}
      >
        <Badge bg={q.nivel === 'principal' ? 'primary' : 'secondary'} style={{ fontSize: '0.65rem' }}>
          {q.nivel === 'principal' ? 'P' : 'C'}
        </Badge>
        {cond && <span className="text-muted small text-nowrap">Se {cond.resposta_valor} →</span>}
        <span style={{ fontSize: '0.85rem' }}>{q.pergunta_text}</span>
        <Badge bg={tipoColor(q.tipo_resposta)} className="ms-auto text-nowrap">{tipoLabel(q.tipo_resposta)}</Badge>
      </div>
    );
  };

  // ── Main render ───────────────────────────────────────────────────────────

  if (loading) {
    return <Container className="py-5 text-center"><Spinner animation="border" /></Container>;
  }

  const principalsList = questions.filter(q => q.nivel === 'principal');
  const effectiveFocusedTempId = principalsList.some(p => p.temp_id === focusedTempId) ? focusedTempId : null;

  return (
    <Container fluid className="py-3 py-md-4">
      {/* Title */}
      <div className="d-flex align-items-center gap-2 mb-3 mb-md-4 flex-wrap">
        <h1 className="mb-0 fs-4 fs-md-3">{isEditing ? 'Editar Questionário' : 'Cadastro de Questionários'}</h1>
        {isModelo && <Badge bg="info">Modelo</Badge>}
        {isEditing && canWrite && (
          <Button variant="outline-secondary" size="sm" className="ms-auto" onClick={() => setShowClone(true)}>
            Clonar
          </Button>
        )}
      </div>

      {!canWrite && (
        <Alert variant="warning">Você tem acesso somente de <strong>leitura</strong> neste módulo.</Alert>
      )}
      {error && <Alert variant="danger">{error}</Alert>}
      {!empresaId && (
        <Alert variant="danger">Nenhuma empresa ativa. Selecione uma empresa para continuar.</Alert>
      )}

      {/* ── Header info ── */}
      <Card className="mb-4">
        <Card.Header as="h5">Informações Básicas</Card.Header>
        <Card.Body>
          <Row className="g-3">
            <Col xs={12} sm={6} md={5}>
              <Form.Label>Nome <span className="text-danger">*</span></Form.Label>
              <Form.Control
                value={nome}
                onChange={e => setNome(e.target.value)}
                placeholder="Nome do questionário"
                disabled={!canWrite}
              />
            </Col>
            <Col xs={12} sm={6} md={5}>
              <Form.Label>Descrição</Form.Label>
              <Form.Control
                value={descricao}
                onChange={e => setDescricao(e.target.value)}
                placeholder="Descrição"
                disabled={!canWrite}
              />
            </Col>
            <Col xs={6} sm={3} md={2} className="d-flex align-items-end pb-1">
              <Form.Check
                type="switch"
                label="É modelo?"
                checked={isModelo}
                onChange={e => setIsModelo(e.target.checked)}
                disabled={!canWrite}
              />
            </Col>
            <Col xs={12} md={6}>
              <Form.Label>Framework</Form.Label>
              <div className="d-flex gap-2 align-items-start">
                <div className="flex-grow-1">
                  <PredictiveSearch<Framework>
                    items={frameworks}
                    displayKey="nome"
                    value={frameworkNome}
                    placeholder="Pesquisar framework..."
                    onSelect={fw => { setFrameworkId(fw.id); setFrameworkNome(fw.nome); }}
                    onClear={() => { setFrameworkId(null); setFrameworkNome(''); }}
                    disabled={!canWrite}
                  />
                </div>
                <Button
                  variant="outline-secondary"
                  onClick={() => { setFrameworkSearch(''); setShowFrameworkModal(true); }}
                  disabled={!canWrite}
                  title="Ver todos os frameworks"
                  style={{ flexShrink: 0 }}
                >
                  🔍
                </Button>
              </div>
            </Col>
            <Col xs={12} md={6}>
              <Form.Label>Escopo do Questionário</Form.Label>
              <PredictiveSearch<{ id: number; nome: string }>
                items={escopoItems}
                displayKey="nome"
                value={escopo}
                placeholder="Pesquisar escopo..."
                onSelect={item => setEscopo(item.nome)}
                onClear={() => setEscopo('')}
                disabled={!canWrite}
              />
              <Form.Text className="text-muted">
                Usado para relacionar o questionário com o fornecedor.
              </Form.Text>
            </Col>
          </Row>
          {isEditing && (
            <Row className="mt-3">
              <Col xs={12}>
                <Button
                  variant="primary"
                  disabled={!podeEnviarQuestionario}
                  onClick={abrirEnviarModal}
                >
                  <FiSend className="me-1" /> Enviar Questionário aos Fornecedores
                </Button>
                <div className="text-muted small mt-1">
                  {isModelo
                    ? 'Questionários modelo não podem ser enviados — clone antes de enviar.'
                    : isFechado
                    ? 'Este questionário está fechado e não pode mais ser enviado.'
                    : escopo.trim() === ''
                    ? 'Defina um Escopo do Questionário para poder enviá-lo aos fornecedores.'
                    : 'O fornecedor receberá um link por e-mail para responder este questionário.'}
                </div>
              </Col>
            </Row>
          )}
        </Card.Body>
      </Card>

      {/* ── Questions section ── */}
      <Card className="mb-4">
        <Card.Header as="h5" className="d-flex justify-content-between align-items-center">
          Perguntas do Questionário
          {canWrite && (
            <Button
              variant="outline-primary"
              size="sm"
              onClick={() => setShowPicker(true)}
            >
              + Adicionar Pergunta Principal
            </Button>
          )}
        </Card.Header>
        <Card.Body>
          {principalsList.length === 0 ? (
            <p className="text-muted fst-italic mb-0">
              Nenhuma pergunta adicionada. Clique em "+ Adicionar Pergunta Principal" para começar.
            </p>
          ) : effectiveFocusedTempId ? (
            <>
              <div className="d-flex justify-content-between align-items-center mb-2">
                <span className="text-muted small">Editando um bloco de perguntas.</span>
                <Button variant="link" size="sm" onClick={() => setFocusedTempId(null)}>
                  Mostrar todas as perguntas
                </Button>
              </div>
              {getBlockItems(effectiveFocusedTempId).map(q => renderQuestionCard(q, depthOf(q)))}
              {principalsList.filter(p => p.temp_id !== effectiveFocusedTempId).map(renderCollapsedBlockRow)}
            </>
          ) : (
            // Nenhum bloco em edição: mostra todos os blocos recolhidos (grid), mantendo
            // a tela curta. Clicar em "Editar bloco" expande o bloco correspondente.
            <>
              <div className="text-muted small mb-2">
                Selecione um bloco para editar ou clique em "+ Adicionar Pergunta Principal".
              </div>
              {principalsList.map(renderCollapsedBlockRow)}
            </>
          )}
        </Card.Body>
      </Card>

      {/* ── Preview section ── */}
      <Card className="mb-4">
        <Card.Header as="h5">Visualização do Questionário</Card.Header>
        <Card.Body>
          {questions.length === 0 ? (
            <p className="text-muted fst-italic mb-0">Nenhuma pergunta configurada ainda.</p>
          ) : (
            questions.map(q => renderPreviewItem(q))
          )}
        </Card.Body>
      </Card>

      {/* ── Action buttons ── */}
      <div className="text-end">
        <Button variant="secondary" onClick={() => navigate('/app/questionarios/list')} className="me-2">
          Cancelar
        </Button>
        {canWrite && (
          <Button
            variant="primary"
            onClick={handleSave}
            disabled={saving || !empresaId}
          >
            {saving ? <Spinner as="span" size="sm" animation="border" /> : isEditing ? 'Atualizar' : 'Salvar'}
          </Button>
        )}
      </div>

      {/* ── Framework picker modal ── */}
      <Modal show={showFrameworkModal} onHide={() => setShowFrameworkModal(false)} size="lg" centered>
        <Modal.Header closeButton><Modal.Title>Selecionar Framework</Modal.Title></Modal.Header>
        <Modal.Body>
          <Form.Control
            className="mb-3"
            placeholder="Filtrar frameworks..."
            value={frameworkSearch}
            onChange={e => setFrameworkSearch(e.target.value)}
            autoFocus
          />
          <ListGroup style={{ maxHeight: 400, overflowY: 'auto' }}>
            {frameworks.length === 0 && (
              <ListGroup.Item className="text-muted">Nenhum framework cadastrado</ListGroup.Item>
            )}
            {frameworks
              .filter(fw => fw.nome.toLowerCase().includes(frameworkSearch.toLowerCase()))
              .map(fw => (
                <ListGroup.Item
                  key={fw.id}
                  action
                  active={fw.id === frameworkId}
                  onClick={() => {
                    setFrameworkId(fw.id);
                    setFrameworkNome(fw.nome);
                    setShowFrameworkModal(false);
                  }}
                >
                  <div className="fw-semibold">{fw.nome}</div>
                  {fw.descricao && <div className="text-muted small">{fw.descricao}</div>}
                </ListGroup.Item>
              ))}
          </ListGroup>
        </Modal.Body>
      </Modal>

      {/* ── Question picker modal ── */}
      <QuestionPickerModal
        show={showPicker}
        onHide={() => setShowPicker(false)}
        perguntas={filteredPerguntas.filter(p => !perguntaIdsUsadas.has(p.id))}
        onSelect={addQuestion}
        title="Adicionar Pergunta Principal"
      />

      {/* ── Clone modal ── */}
      <CloneQuestionnaireModal
        show={showClone}
        onHide={() => setShowClone(false)}
        onConfirm={handleClone}
        originalName={nome}
        saving={cloning}
        empresaId={empresaId}
      />

      {/* ── Checklist: escolha e edição de itens ── */}
      <ChecklistChoiceModal
        show={!!checklistChoice}
        onHide={() => setChecklistChoice(null)}
        onConfirm={handleChecklistChoice}
      />
      <ChecklistEditModal
        show={!!checklistEdit}
        perguntaText={checklistEdit ? (questions.find(q => q.temp_id === checklistEdit.tempId)?.pergunta_text || '') : ''}
        pesoPergunta={checklistEdit ? (questions.find(q => q.temp_id === checklistEdit.tempId)?.peso ?? 100) : 100}
        itensIniciais={checklistEdit?.itens || []}
        onCancel={() => setChecklistEdit(null)}
        onSave={handleChecklistEditSave}
      />

      {/* ── Enviar Questionário aos Fornecedores ── */}
      <Modal
        show={showEnviarModal}
        onHide={progressoEnvio && !progressoFinalizado ? undefined : fecharEnviarModal}
        backdrop={progressoEnvio && !progressoFinalizado ? 'static' : true}
        centered
        size="lg"
      >
        {!progressoEnvio ? (
          <>
            <Modal.Header closeButton>
              <Modal.Title>Enviar Questionário</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <p className="text-muted small">
                Selecione os fornecedores que devem receber o link para o questionário.
              </p>
              {carregandoFornecedores ? (
                <div className="text-center py-3"><Spinner animation="border" size="sm" /></div>
              ) : fornecedoresEscopo.length === 0 ? (
                <div className="text-muted small mb-3">
                  Nenhum fornecedor com o escopo "{escopo}" cadastrado ainda.
                </div>
              ) : (
                <div style={{ overflowX: 'auto' }}>
                  <Table size="sm" bordered className="mb-3">
                    <thead>
                      <tr>
                        <th style={{ width: 36 }}>
                          <Form.Check
                            title="Selecionar Todos"
                            checked={fornecedoresEscopo.length > 0 && fornecedoresEscopo.every(l => selecaoEnvio[l.contrato_escopo_id])}
                            onChange={e => selecionarTodosEnvio(e.target.checked)}
                          />
                        </th>
                        <th>Nome do Fornecedor</th>
                        <th>Nome do Responsável</th>
                        <th>E-mail do Responsável</th>
                        <th>Nome do Questionário</th>
                      </tr>
                    </thead>
                    <tbody>
                      {fornecedoresEscopo.map(l => (
                        <tr key={l.contrato_escopo_id}>
                          <td>
                            <Form.Check
                              checked={!!selecaoEnvio[l.contrato_escopo_id]}
                              onChange={e => setSelecaoEnvio(prev => ({ ...prev, [l.contrato_escopo_id]: e.target.checked }))}
                            />
                          </td>
                          <td>{l.fornecedor_nome}</td>
                          <td>{l.representante_nome}</td>
                          <td>{l.representante_email}</td>
                          <td>{nome}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              )}
              <Form.Group>
                <Form.Label>Prazo alvo para resposta (opcional, aplicado aos selecionados)</Form.Label>
                <Form.Control type="date" value={prazoAlvoEnvio} onChange={e => setPrazoAlvoEnvio(e.target.value)} />
              </Form.Group>
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={fecharEnviarModal}>Cancelar</Button>
              <Button
                variant="primary"
                onClick={confirmarEnvioFornecedores}
                disabled={carregandoFornecedores || fornecedoresEscopo.length === 0}
              >
                Confirmar e Enviar
              </Button>
            </Modal.Footer>
          </>
        ) : (
          <>
            <Modal.Header closeButton={progressoFinalizado} className="bg-dark text-white border-0">
              <Modal.Title>Enviando questionários</Modal.Title>
            </Modal.Header>
            <Modal.Body className="bg-dark text-white">
              {progressoEnvio.slice(paginaProgresso * 3, paginaProgresso * 3 + 3).map(item => {
                const cor = item.status === 'enviado' ? '#2ecc71' : item.status === 'erro' ? '#e74c3c' : item.status === 'enviando' ? '#5dade2' : '#95a5a6';
                const texto = item.status === 'enviado' ? 'Enviado' : item.status === 'erro' ? 'Falha ao enviar'
                  : item.status === 'enviando' ? 'Criando link e enviando...' : 'Aguardando...';
                return (
                  <div
                    key={item.contrato_escopo_id}
                    className="d-flex align-items-center gap-3 p-2 mb-2 rounded"
                    style={{ background: 'rgba(255,255,255,0.06)' }}
                  >
                    <span style={{ color: cor, fontSize: '1.1rem', width: 20 }}>
                      {item.status === 'enviado' && <FiCheck />}
                      {item.status === 'erro' && <FiXCircle />}
                      {item.status === 'enviando' && <Spinner as="span" size="sm" animation="border" style={{ color: cor }} />}
                      {item.status === 'aguardando' && <FiClock />}
                    </span>
                    <div>
                      <div className="fw-semibold">{item.fornecedor_nome}</div>
                      <div className="small" style={{ color: cor }}>{texto}</div>
                    </div>
                  </div>
                );
              })}
              {totalPaginasProgresso > 1 && (
                <div className="d-flex justify-content-between align-items-center mt-2">
                  <Button
                    variant="outline-light" size="sm"
                    disabled={paginaProgresso === 0}
                    onClick={() => setPaginaProgresso(p => Math.max(0, p - 1))}
                  >
                    ‹ Anterior
                  </Button>
                  <span className="small text-muted">Página {paginaProgresso + 1} de {totalPaginasProgresso}</span>
                  <Button
                    variant="outline-light" size="sm"
                    disabled={paginaProgresso >= totalPaginasProgresso - 1}
                    onClick={() => setPaginaProgresso(p => Math.min(totalPaginasProgresso - 1, p + 1))}
                  >
                    Próximo ›
                  </Button>
                </div>
              )}
              <div className="text-center text-muted small mt-3">
                {progressoConcluidos} de {progressoEnvio.length} concluídos
              </div>
            </Modal.Body>
            <Modal.Footer className="bg-dark border-0">
              <Button variant="primary" disabled={!progressoFinalizado} onClick={fecharEnviarModal}>
                {progressoFinalizado ? 'Concluir' : <Spinner as="span" size="sm" animation="border" />}
              </Button>
            </Modal.Footer>
          </>
        )}
      </Modal>
    </Container>
  );
};

export default QuestionnaireRegistration;
